/**
 * Comprime y redimensiona una imagen en el navegador antes de subirla.
 * Reduce drásticamente el peso (una foto de móvil de ~5MB pasa a ~200-400KB),
 * ahorrando almacenamiento (GridFS) y ancho de banda, y permitiendo escalar a
 * miles de artículos con fotos.
 *
 * @param {File} file - archivo original
 * @param {Object} opts - { maxDimension=1600, quality=0.82, mimeType='image/jpeg' }
 * @returns {Promise<File>} archivo comprimido (o el original si no aplica/no mejora)
 */
export async function compressImage(file, opts = {}) {
  const { maxDimension = 1600, quality = 0.82, mimeType = 'image/jpeg' } = opts;

  // No tocar: no-imágenes ni GIF (animación) ni SVG (vectorial)
  if (!file.type.startsWith('image/') || file.type === 'image/gif' || file.type === 'image/svg+xml') {
    return file;
  }

  try {
    const dataUrl = await readAsDataURL(file);
    const img = await loadImage(dataUrl);

    let { width, height } = img;
    if (width > maxDimension || height > maxDimension) {
      if (width >= height) {
        height = Math.round((height * maxDimension) / width);
        width = maxDimension;
      } else {
        width = Math.round((width * maxDimension) / height);
        height = maxDimension;
      }
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    // Fondo blanco para imágenes con transparencia al pasar a JPEG
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, mimeType, quality));
    if (!blob || blob.size >= file.size) {
      // No mejoró el peso; conservar original
      return file;
    }

    const newName = file.name.replace(/\.(png|jpe?g|webp|bmp|heic|heif)$/i, '') + '.jpg';
    return new File([blob], newName, { type: mimeType, lastModified: Date.now() });
  } catch (e) {
    // Si algo falla, subir el original sin bloquear al usuario
    return file;
  }
}

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
